// Group-chat "delivered" receipts, written sparingly.
//
// The receipt lives on the rooms ROW (`delivered_at[userId]`), and every open
// tab is bound to the whole rooms table. So each receipt is a rooms UPDATE that
// Realtime then checks, per subscriber, against the room policy. The old effect
// wrote one the instant a message arrived — from EVERY tab and device of EVERY
// member, for EVERY message. Production (2026-09-21): 5,095 rooms updates for
// 473 messages, about eleven per message, the single largest source of Realtime
// load and the one that grows with (people online)².
//
// This queue keeps the behaviour (the sender's grey ✓✓ still appears) and cuts
// the writes:
//   • COALESCE — wait a few seconds, so a burst of messages is one receipt;
//   • JITTER + RE-CHECK — each tab waits a different random extra, then looks at
//     the room again before writing. The first tab's write reaches the others
//     through the rooms listener, so they find the receipt already there and
//     stay silent: one write per PERSON, not one per tab/device;
//   • READ COVERS DELIVERED — a member who has the room open writes a read
//     receipt at once (RoomChat), and `deliveredStamp = max(delivered, read)`,
//     so by flush time nothing is left to write.
// Until receipts move off the rooms row altogether (LANE1_PLAN.md) this is the
// cheapest large cut, and it needs no migration and no mobile change.
import type { ChatRoom } from '../types';

export const DELIVERY_COALESCE_MS = 3000;
export const DELIVERY_JITTER_MS = 2500;

type RoomLike = Pick<ChatRoom, 'id' | 'lastMessageAt' | 'deliveredAt' | 'readAt'>;

export interface DeliveryReceiptQueue {
  /** Call on every rooms change. Cheap; never writes synchronously. */
  offer(rooms: RoomLike[]): void;
  dispose(): void;
}

export function createDeliveryReceiptQueue(opts: {
  me: string;
  /** Latest known state of a room at flush time (the re-check). */
  getRoom: (roomId: string) => RoomLike | undefined;
  write: (roomId: string, at: number) => Promise<void>;
  coalesceMs?: number;
  jitterMs?: number;
  random?: () => number;
  onError?: (e: unknown) => void;
}): DeliveryReceiptQueue {
  const coalesce = opts.coalesceMs ?? DELIVERY_COALESCE_MS;
  const jitter = opts.jitterMs ?? DELIVERY_JITTER_MS;
  const rnd = opts.random ?? Math.random;
  const pending = new Map<string, number>();
  const written = new Map<string, number>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const stampOf = (r: RoomLike) => Math.max(r.deliveredAt?.[opts.me] ?? 0, r.readAt?.[opts.me] ?? 0);

  const flush = () => {
    timer = null;
    if (disposed) return;
    const batch = [...pending.entries()];
    pending.clear();
    for (const [roomId, queuedAt] of batch) {
      const room = opts.getRoom(roomId);
      if (!room) continue;                                   // left the room meanwhile
      const target = Math.max(queuedAt, room.lastMessageAt ?? 0);
      if (stampOf(room) >= target) continue;                 // another tab, or a read receipt, got there first
      if ((written.get(roomId) ?? 0) >= target) continue;    // our own write is still echoing back
      written.set(roomId, target);
      opts.write(roomId, target).catch((e) => {
        written.delete(roomId);                              // a later snapshot retries — best-effort, never fatal
        opts.onError?.(e);
      });
    }
  };

  return {
    offer(rooms) {
      if (disposed) return;
      for (const room of rooms) {
        const at = room.lastMessageAt;
        if (!at) continue;
        if (stampOf(room) >= at) continue;
        if ((written.get(room.id) ?? 0) >= at) continue;
        if ((pending.get(room.id) ?? 0) < at) pending.set(room.id, at);
      }
      if (pending.size && !timer) timer = setTimeout(flush, coalesce + rnd() * jitter);
    },
    dispose() {
      disposed = true;
      if (timer) { clearTimeout(timer); timer = null; }
      pending.clear();
    },
  };
}
